import React, {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import AgoraRTC, { IAgoraRTCRemoteUser, IRemoteAudioTrack, IRemoteVideoTrack } from 'agora-rtc-sdk-ng';
import { fromEvent, interval, merge } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { LocalizableString } from 'models/cms/common/types';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { IconButton, Slider } from '@material-ui/core';
import {
  Close, Fullscreen, FullscreenExit, KeyboardArrowDown, KeyboardArrowUp, VolumeOff, VolumeUp,
} from '@material-ui/icons';
import { liveStreamActions, liveStreamSelectors } from 'models/liveStream';
import { AGORA_APP_ID } from 'appenv';
import screenfull from 'screenfull';
import { createSelector } from 'reselect';

const StyledContainer = styled.div<{ hide?: boolean, isExpanded: boolean }>`
  width: 100%;
  padding: 5px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  ${({ hide }) => (hide ? 'display: none;' : 'display: flex;')}
  flex-direction: column;
  transition: all 250ms ease;
  height: ${({ isExpanded }) => (isExpanded ? '100vh' : '42px')};
  overflow: hidden;

  &:-webkit-full-screen {
    width: 100%;
    height: 100%;
    max-height: 100%;
  }
`;

const LiveTag = styled.div`
  background: #cf2f2f;
  padding: 4px 8px;
  border-radius: 4px;
  margin-right: 8px;
`;

const StyledTitleRow = styled.div<{ enableHandle: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  ${({ enableHandle }) => (enableHandle ? 'cursor: move;' : '')}
`;

const StyledTitleRowName = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
`;

const StyledTitleBoothName = styled.div`
  flex: 1 1 auto;
  width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTitleRowActions = styled.div`
  display: flex;
  align-items: center;
`;

const StyledPlayerControlBar = styled.div<{ isFullscreen: boolean }>`
  position: absolute;
  align-items: center;
  display: flex;
  width: 100%;
  height: ${({ isFullscreen }) => (isFullscreen ? '48' : 24)}px;
  background: #000000;
  bottom: 0;
  transition: all 250ms;
  justify-content: space-between;
`;

const StyledPlayerOuterContainer = styled.div`
  position: relative;
  flex: 1 0 auto;
  overflow: hidden;

  ${StyledPlayerControlBar} {
    opacity: 0;
  }

  &:hover {
    ${StyledPlayerControlBar} {
      opacity: 0.7;
    }
  }
`;

const StyledPlayerContainer = styled.div<{ hide?: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;

  ${({ hide }) => (hide ? 'display: none;' : '')}
`;

const StyledVolumeControl = styled.div<{ isFullscreen: boolean }>`
  display: flex;
  align-items: center;
  width: ${({ isFullscreen }) => (isFullscreen ? '150px' : '100px')};

  & .MuiSlider-root {
    color: white !important;
  }
`;

const StyledAudioOnlyContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: #545454;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
`;

export interface PictureInPictureBoothLiveStreamContentProps {
  boothId: string;
  agoraToken: string;
  agoraAccount: string;
  boothName: LocalizableString;
  isClosable: boolean;
  className: string;
  enableHandle?: boolean;
}

const PictureInPictureBoothLiveStreamContent: FC<PictureInPictureBoothLiveStreamContentProps> = ({
  boothId,
  agoraToken,
  agoraAccount,
  boothName,
  isClosable,
  className,
  enableHandle,
}) => {
  const playerRef = useRef<HTMLDivElement>();

  const client = useMemo(() => AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' }), []);

  const [videoStreaming, setVideoStreaming] = useState(false);

  const [audioTracks, setAudioTracks] = useState<IRemoteAudioTrack[]>([]);
  const audioStreaming = audioTracks.length > 0;

  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(80);

  const handleVolumeChange = useCallback((e, newValue) => {
    setVolumeLevel(newValue);
    audioTracks.forEach((track) => track.setVolume(newValue));
  }, [audioTracks]);

  const handleAudioMuteToggle = useCallback(() => {
    if (isAudioMuted) audioTracks.forEach((track) => track.setVolume(volumeLevel));
    else audioTracks.forEach((track) => track.setVolume(0));
    setIsAudioMuted(!isAudioMuted);
  }, [audioTracks, isAudioMuted, volumeLevel]);

  useEffect(() => {
    const trackSubscriber = () => fromEvent(client, 'user-published').pipe(
      mergeMap(([user, mediaType]: [IAgoraRTCRemoteUser, 'audio' | 'video']) => client.subscribe(user, mediaType).then((track) => {
        if (track.trackMediaType === 'video') {
          (track as IRemoteVideoTrack).play(playerRef.current, { fit: 'contain' });
          setVideoStreaming(true);
        } else {
          track.play();
          (track as IRemoteAudioTrack).setVolume(volumeLevel);
          setAudioTracks([...audioTracks, track as IRemoteAudioTrack]);
        }
      })),
    );

    const trackUnpublished = () => fromEvent(client, 'user-unpublished').pipe(
      tap(([user, mediaType]: [IAgoraRTCRemoteUser, 'audio' | 'video']) => {
        if (mediaType === 'video') {
          setVideoStreaming(false);
        } else {
          setAudioTracks(audioTracks.filter((track) => track.getUserId() !== user.uid));
        }
      }),
    );

    const disposable = merge(trackSubscriber(), trackUnpublished()).subscribe();
    return () => disposable.unsubscribe();
  }, [audioTracks, client, volumeLevel]);

  const isLiveStreamingSelector = useMemo(() => createSelector(liveStreamSelectors.selectBoothLiveStreamingStatus, (status) => status[boothId]), []);
  const isLiveStreaming = useSelector(isLiveStreamingSelector);

  const agoraTokenRef = useRef(agoraToken);

  useEffect(() => {
    agoraTokenRef.current = agoraToken;
  }, [agoraToken]);

  useEffect(() => {
    let joined = false;

    const join = () => {
      joined = true;
      return client.join(AGORA_APP_ID, `booth-${boothId}`, agoraTokenRef.current, agoraAccount);
    };
    const leave = () => {
      joined = false;
      setAudioTracks([]);
      setVideoStreaming(false);
      return client.leave();
    };

    const disposable = interval(500).subscribe(() => {
      if (isLiveStreaming && !joined) join();
      else if (!isLiveStreaming && joined) leave();
    });

    return () => {
      if (joined) leave();

      disposable.unsubscribe();
    };
  }, [agoraAccount, boothId, client, isLiveStreaming]);

  useEffect(() => {
    if (client.channelName != null && client) client.renewToken(agoraToken);
  }, [client, agoraToken]);

  const dispatch = useDispatch();
  const handleClose = useCallback(() => {
    dispatch(liveStreamActions.joinChannel(null));
  }, [dispatch]);

  const locale = useSelector(selectLocale);

  const [containerEl, setContainerEl] = useState<HTMLDivElement>();

  const containerElRef = useCallback((element) => setContainerEl(element), []);

  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (containerEl != null) {
      const disposable = fromEvent(containerEl, 'fullscreenchange').subscribe((e) => {
        setIsFullScreen(document.fullscreenElement === containerEl);
      });
      return () => disposable.unsubscribe();
    }
    return undefined;
  }, [containerEl]);

  const handleFullscreenToggle = useCallback(() => {
    if (screenfull.isEnabled && screenfull.isFullscreen) screenfull.exit();
    else if (containerEl != null) {
      if (!screenfull.isEnabled) {
        alert('Your browser or device do not support fullscreen mode');
      } else {
        screenfull.request(containerEl);
      }
    }
  }, [containerEl]);

  const [isExpanded, setIsExpanded] = useState(true);

  const handleExpandToggle = useCallback(() => setIsExpanded(!isExpanded), [isExpanded]);
  return (
    <StyledContainer
      hide={(!videoStreaming && !audioStreaming)}
      ref={containerElRef}
      isExpanded={isExpanded}
      className={className}
    >
      <StyledTitleRow className={enableHandle ? 'handle' : ''} enableHandle={enableHandle}>
        <StyledTitleRowName>
          <LiveTag>LIVE</LiveTag>
          <StyledTitleBoothName>{boothName?.[locale]}</StyledTitleBoothName>
        </StyledTitleRowName>
        {!isFullScreen && (
          <StyledTitleRowActions>
            <IconButton size="small" onClick={handleExpandToggle}>
              {isExpanded
                ? <KeyboardArrowUp fontSize="inherit" htmlColor="white" />
                : <KeyboardArrowDown fontSize="inherit" htmlColor="white" />}
            </IconButton>
            {isClosable && (
              <IconButton size="small" onClick={handleClose}>
                <Close fontSize="inherit" htmlColor="white" />
              </IconButton>
            )}
          </StyledTitleRowActions>
        )}
      </StyledTitleRow>
      <StyledPlayerOuterContainer>
        <StyledPlayerContainer ref={playerRef} hide={!videoStreaming} />
        {!videoStreaming && audioStreaming && (<StyledAudioOnlyContainer>Audio Streaming</StyledAudioOnlyContainer>)}
        <StyledPlayerControlBar isFullscreen={isFullScreen}>
          <StyledVolumeControl isFullscreen={isFullScreen}>
            <IconButton onClick={handleAudioMuteToggle}>
              {isAudioMuted ? <VolumeOff htmlColor="white" /> : <VolumeUp htmlColor="white" />}
            </IconButton>
            <Slider
              value={volumeLevel}
              onChange={handleVolumeChange}
              color="primary"
            />
          </StyledVolumeControl>
          <IconButton size="small" onClick={handleFullscreenToggle}>
            {
              isFullScreen
                ? <FullscreenExit htmlColor="white" />
                : <Fullscreen htmlColor="white" />
            }
          </IconButton>
        </StyledPlayerControlBar>
      </StyledPlayerOuterContainer>
    </StyledContainer>
  );
};

export default React.memo(PictureInPictureBoothLiveStreamContent);
